import { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import {
  Plus, X, FileCheck, Sparkles, Copy, Trash2, GripVertical,
  Type, AlignLeft, Calendar, ChevronDown, CheckSquare, Circle,
  PenLine, Heading, Info, Search, Send, Eye, EyeOff, Download, Clock,
  Globe, Settings2, Users, UserPlus, User, Baby, Upload, FileText
} from "lucide-react";
import SignaturePad from "@/components/SignaturePad";
import AuditTrailPanel, { ukDate } from "@/components/AuditTrailPanel";
import GuardianDetailsPanel from "@/components/GuardianDetailsPanel";
import { FormReviewPanel } from "@/components/FormReviewPanel";
import PdfTemplateViewer from "@/components/PdfTemplateViewer";
import PDFUploadModal from "@/components/PDFUploadModal";
import ContextualHelp from "@/components/ContextualHelp";

const H = { fontFamily: "'Cabinet Grotesk', sans-serif" };
const FIELD_TYPES = [
  { type: "heading", label: "Section heading", icon: Heading },
  { type: "info_text", label: "Info text", icon: Info },
  { type: "short_text", label: "Short text", icon: Type },
  { type: "long_text", label: "Long text", icon: AlignLeft },
  { type: "date", label: "Date", icon: Calendar },
  { type: "dropdown", label: "Dropdown", icon: ChevronDown },
  { type: "checkbox", label: "Checkbox", icon: CheckSquare },
  { type: "radio", label: "Radio buttons", icon: Circle },
  { type: "signature", label: "Signature", icon: PenLine },
];
const TYPE_BADGE = { consent: "bg-[#205B5A]/10 text-[#205B5A]", intake: "bg-[#496275]/10 text-[#496275]", treatment_info: "bg-amber-50 text-amber-600", privacy: "bg-blue-50 text-blue-600", custom: "bg-[#1A1B1A]/5 text-[#757675]" };

/* ── Form Builder ── */
function FormBuilder({ initial, onSave, onClose }) {
  const [name, setName] = useState(initial?.name || "");
  const [desc, setDesc] = useState(initial?.description || "");
  const [formType, setFormType] = useState(initial?.form_type || "consent");
  const [fields, setFields] = useState(initial?.fields || []);
  const [showAI, setShowAI] = useState(Boolean(initial?.aiStart));
  const [aiPrompt, setAiPrompt] = useState("");
  const [aiLoading, setAiLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  // Assignment rules
  const [isActive, setIsActive] = useState(initial?.is_active !== false);
  const [appliesToAll, setAppliesToAll] = useState(initial?.applies_to_all || false);
  const [appliesToNew, setAppliesToNew] = useState(initial?.applies_to_new_patients || false);
  const [appliesToReturning, setAppliesToReturning] = useState(initial?.applies_to_returning_patients || false);
  const [appliesToUnder18, setAppliesToUnder18] = useState(initial?.applies_to_under_18 || false);
  const [minorMode, setMinorMode] = useState(initial?.minor_mode || "auto");
  const [assignedApptTypes, setAssignedApptTypes] = useState(initial?.assigned_appointment_type_ids || []);
  const [apptTypes, setApptTypes] = useState([]);

  // Load appointment types
  useEffect(() => {
    api.get("/appointment-types").then(res => setApptTypes(res.data.appointment_types || [])).catch(() => {});
  }, []);

  const addField = (type) => {
    const f = { id: crypto.randomUUID(), field_type: type, label: "", required: false, options: type === "dropdown" || type === "radio" || type === "checkbox" ? ["Option 1"] : undefined, placeholder: "" };
    setFields([...fields, f]);
  };

  const updateField = (idx, key, val) => { const nf = [...fields]; nf[idx] = { ...nf[idx], [key]: val }; setFields(nf); };
  const removeField = (idx) => setFields(fields.filter((_, i) => i !== idx));
  const moveField = (idx, dir) => {
    const nf = [...fields];
    const ni = idx + dir;
    if (ni < 0 || ni >= nf.length) return;
    [nf[idx], nf[ni]] = [nf[ni], nf[idx]];
    setFields(nf);
  };

  const handleAIGenerate = async () => {
    if (!aiPrompt.trim()) return;
    setAiLoading(true);
    try {
      const { data } = await api.post("/forms/ai-generate", { description: aiPrompt });
      setName(data.name);
      setFormType(data.form_type);
      setFields(data.fields || []);
      setDesc(aiPrompt);
      setShowAI(false);
    } catch (e) {
      console.error("AI generation failed:", e);
    }
    setAiLoading(false);
  };

  const handleSave = async () => {
    if (!name.trim()) return;
    setSaving(true);
    await onSave({ 
      name, 
      description: desc, 
      form_type: formType, 
      fields,
      is_active: isActive,
      applies_to_all: appliesToAll,
      applies_to_new_patients: appliesToNew,
      applies_to_returning_patients: appliesToReturning,
      applies_to_under_18: appliesToUnder18,
      minor_mode: minorMode,
      assigned_appointment_type_ids: assignedApptTypes,
    });
    setSaving(false);
  };

  const toggleApptType = (typeId) => {
    setAssignedApptTypes(prev => 
      prev.includes(typeId) 
        ? prev.filter(id => id !== typeId) 
        : [...prev, typeId]
    );
  };

  return (
    <div className="fixed inset-0 z-50 bg-[#FAF9F6] flex flex-col" data-testid="form-builder">
      {/* Top bar */}
      <div className="bg-white border-b border-[#1A1B1A]/[0.06] px-5 py-3 flex items-center justify-between flex-shrink-0">
        <div className="flex items-center gap-3">
          <button onClick={onClose} className="p-1 text-[#757675] hover:text-[#1A1B1A]"><X className="w-4 h-4" /></button>
          <input value={name} onChange={e => setName(e.target.value)} placeholder="Form name" data-testid="form-name-input"
            className="text-base font-medium text-[#1A1B1A] bg-transparent border-none outline-none" style={H} />
        </div>
        <div className="flex items-center gap-2">
          <button onClick={() => setShowSettings(true)} 
            className="text-[11px] px-3 py-1.5 rounded-full border border-[#1A1B1A]/10 text-[#4A4B4A] hover:bg-[#FAF9F6] flex items-center gap-1.5"
            data-testid="form-settings-btn">
            <Settings2 className="w-3 h-3" /> Settings
          </button>
          <select value={formType} onChange={e => setFormType(e.target.value)} data-testid="form-type-select"
            className="text-[11px] px-3 py-1.5 rounded-full border border-[#1A1B1A]/10 text-[#4A4B4A]">
            <option value="consent">Consent</option><option value="intake">Intake</option><option value="treatment_info">Treatment info</option><option value="privacy">Privacy</option><option value="custom">Custom</option>
          </select>
          <button onClick={handleSave} disabled={saving || !name} data-testid="save-form-btn"
            className="bg-[#205B5A] text-white px-4 py-1.5 rounded-full text-[11px] font-medium hover:bg-[#174544] disabled:opacity-40">{saving ? "Saving..." : initial?.id ? "Update form" : "Save form"}</button>
        </div>
      </div>

      <div className="flex flex-1 overflow-hidden">
        {/* Left: Field palette */}
        <div className="w-[200px] border-r border-[#1A1B1A]/[0.06] bg-white p-4 overflow-y-auto flex-shrink-0 hidden lg:block">
          <p className="text-[10px] font-semibold text-[#757675] uppercase tracking-wider mb-3">Add fields</p>
          <div className="space-y-1">
            {FIELD_TYPES.map(ft => (
              <button key={ft.type} onClick={() => addField(ft.type)} data-testid={`add-field-${ft.type}`}
                className="w-full text-left px-3 py-2 rounded-lg text-xs font-medium text-[#4A4B4A] hover:bg-[#FAF9F6] flex items-center gap-2 transition-colors">
                <ft.icon className="w-3.5 h-3.5 text-[#757675]" strokeWidth={1.5} />{ft.label}
              </button>
            ))}
          </div>
          <div className="mt-4 pt-4 border-t border-[#1A1B1A]/[0.06]">
            <button onClick={() => setShowAI(true)} data-testid="ai-generate-btn"
              className="w-full px-3 py-2.5 rounded-lg text-xs font-medium text-[#205B5A] bg-[#205B5A]/[0.06] hover:bg-[#205B5A]/10 flex items-center gap-2 transition-colors">
              <Sparkles className="w-3.5 h-3.5" /> Generate with AI
            </button>
          </div>
        </div>

        {/* Center: Form editor */}
        <div className="flex-1 overflow-y-auto p-6">
          <div className="max-w-2xl mx-auto">
            {/* Description */}
            <input value={desc} onChange={e => setDesc(e.target.value)} placeholder="Form description (optional)"
              className="w-full text-sm text-[#757675] bg-transparent border-none outline-none mb-6" />

            {/* Mobile: Add field button */}
            <div className="lg:hidden mb-4 flex flex-wrap gap-1.5">
              {FIELD_TYPES.slice(0, 5).map(ft => (
                <button key={ft.type} onClick={() => addField(ft.type)} className="px-2.5 py-1 rounded-full text-[10px] font-medium bg-[#FAF9F6] text-[#757675] flex items-center gap-1"><ft.icon className="w-3 h-3" />{ft.label}</button>
              ))}
            </div>

            {/* Fields */}
            {fields.length === 0 ? (
              <div className="text-center py-16">
                <FileCheck className="w-10 h-10 text-[#757675]/20 mx-auto mb-3" />
                <p className="text-sm text-[#757675] mb-1">No fields yet</p>
                <p className="text-xs text-[#757675]">Add fields from the left panel or generate with AI</p>
              </div>
            ) : (
              <div className="space-y-3">
                {fields.map((f, i) => (
                  <div key={f.id} className="bg-white rounded-xl border border-[#1A1B1A]/[0.06] p-4 group" data-testid={`form-field-${i}`}>
                    <div className="flex items-start gap-2">
                      <div className="flex flex-col gap-0.5 pt-1 opacity-0 group-hover:opacity-100 transition-opacity">
                        <button onClick={() => moveField(i, -1)} className="text-[#757675] hover:text-[#1A1B1A]" disabled={i === 0}><GripVertical className="w-3 h-3" /></button>
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-2">
                          <span className="text-[9px] font-medium text-[#757675] bg-[#FAF9F6] px-2 py-0.5 rounded-full capitalize">{f.field_type.replace(/_/g, " ")}</span>
                          {f.field_type !== "heading" && f.field_type !== "info_text" && (
                            <label className="flex items-center gap-1 cursor-pointer">
                              <input type="checkbox" checked={f.required || false} onChange={e => updateField(i, "required", e.target.checked)} className="rounded border-[#1A1B1A]/20 text-[#205B5A] w-3 h-3" />
                              <span className="text-[9px] text-[#757675]">Required</span>
                            </label>
                          )}
                        </div>
                        {f.field_type === "heading" ? (
                          <input value={f.label} onChange={e => updateField(i, "label", e.target.value)} placeholder="Section heading" className="w-full text-base font-medium text-[#1A1B1A] bg-transparent border-none outline-none" style={H} />
                        ) : f.field_type === "info_text" ? (
                          <textarea value={f.label} onChange={e => updateField(i, "label", e.target.value)} placeholder="Information text..." rows={2} className="w-full text-sm text-[#4A4B4A] bg-transparent border-none outline-none resize-none" />
                        ) : (
                          <input value={f.label} onChange={e => updateField(i, "label", e.target.value)} placeholder="Field label" className="w-full text-sm text-[#1A1B1A] bg-transparent border-none outline-none mb-1" />
                        )}
                        {(f.field_type === "dropdown" || f.field_type === "radio" || f.field_type === "checkbox") && f.options && (
                          <div className="mt-2 space-y-1">
                            {f.options.map((opt, oi) => (
                              <div key={oi} className="flex items-center gap-2">
                                <span className="text-[10px] text-[#757675]">{oi + 1}.</span>
                                <input value={opt} onChange={e => { const no = [...f.options]; no[oi] = e.target.value; updateField(i, "options", no); }}
                                  className="flex-1 px-2 py-1 rounded-lg border border-[#1A1B1A]/8 text-xs" />
                                <button onClick={() => { const no = f.options.filter((_, j) => j !== oi); updateField(i, "options", no); }} className="text-[#757675] hover:text-red-500"><X className="w-3 h-3" /></button>
                              </div>
                            ))}
                            <button onClick={() => updateField(i, "options", [...(f.options || []), `Option ${(f.options?.length || 0) + 1}`])}
                              className="text-[10px] text-[#205B5A] font-medium hover:text-[#174544]">+ Add option</button>
                          </div>
                        )}
                        {f.field_type === "signature" && (
                          <div className="mt-2 h-16 border border-dashed border-[#1A1B1A]/15 rounded-lg flex items-center justify-center"><span className="text-xs text-[#757675]">Signature field</span></div>
                        )}
                      </div>
                      <button onClick={() => removeField(i)} className="p-1 text-[#757675] hover:text-red-500 opacity-0 group-hover:opacity-100 transition-opacity"><Trash2 className="w-3.5 h-3.5" /></button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* AI Modal */}
      {showAI && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/30 px-4" data-testid="ai-form-modal">
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-md p-6">
            <div className="flex items-center gap-2 mb-4"><Sparkles className="w-4 h-4 text-[#205B5A]" /><h2 className="text-lg font-medium text-[#1A1B1A]" style={H}>Generate with AI</h2></div>
            <p className="text-xs text-[#757675] mb-3">Describe the form you need and we&apos;ll generate a draft structure for you to review and edit.</p>
            <textarea value={aiPrompt} onChange={e => setAiPrompt(e.target.value)} rows={4} data-testid="ai-prompt-input"
              className="w-full px-3 py-2.5 rounded-xl border border-[#1A1B1A]/10 text-sm focus:outline-none focus:border-[#205B5A] resize-none mb-4"
              placeholder="e.g. Create a first appointment osteopathy consent form including treatment consent, privacy notice, cancellation policy, marketing consent, and patient signature." />
            <div className="flex justify-end gap-3">
              <button onClick={() => setShowAI(false)} className="text-sm text-[#757675]">Cancel</button>
              <button onClick={handleAIGenerate} disabled={aiLoading || !aiPrompt.trim()} data-testid="ai-generate-submit-btn"
                className="bg-[#205B5A] text-white px-5 py-2 rounded-full text-sm font-medium hover:bg-[#174544] disabled:opacity-40 flex items-center gap-1.5">
                <Sparkles className="w-3.5 h-3.5" />{aiLoading ? "Generating..." : "Generate form"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Settings Modal */}
      {showSettings && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/30 px-4" data-testid="form-settings-modal">
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-lg p-6 max-h-[85vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2">
                <Settings2 className="w-4 h-4 text-[#205B5A]" />
                <h2 className="text-lg font-medium text-[#1A1B1A]" style={H}>Form Settings</h2>
              </div>
              <button onClick={() => setShowSettings(false)} className="p-1 text-[#757675] hover:text-[#1A1B1A]">
                <X className="w-4 h-4" />
              </button>
            </div>
            
            {/* Status Toggle */}
            <div className="mb-6">
              <label className="flex items-center justify-between p-3 rounded-xl border border-[#1A1B1A]/[0.06] cursor-pointer hover:bg-[#FAF9F6]">
                <div className="flex items-center gap-3">
                  {isActive ? <Eye className="w-4 h-4 text-[#205B5A]" /> : <EyeOff className="w-4 h-4 text-[#757675]" />}
                  <div>
                    <p className="text-sm font-medium text-[#1A1B1A]">Form Status</p>
                    <p className="text-xs text-[#757675]">{isActive ? "Active - can be assigned to appointments" : "Inactive - won't appear in appointments"}</p>
                  </div>
                </div>
                <div className={`w-10 h-6 rounded-full relative transition-colors ${isActive ? "bg-[#205B5A]" : "bg-[#1A1B1A]/10"}`}
                  onClick={() => setIsActive(!isActive)}>
                  <div className={`absolute top-1 w-4 h-4 rounded-full bg-white shadow transition-transform ${isActive ? "translate-x-5" : "translate-x-1"}`} />
                </div>
              </label>
            </div>

            {/* Assignment Rules */}
            <div className="mb-6">
              <h3 className="text-xs font-semibold text-[#757675] uppercase tracking-wider mb-3">Assignment Rules</h3>
              <div className="space-y-2">
                <label className="flex items-center gap-3 p-3 rounded-xl border border-[#1A1B1A]/[0.06] cursor-pointer hover:bg-[#FAF9F6]">
                  <input type="checkbox" checked={appliesToAll} onChange={e => setAppliesToAll(e.target.checked)} 
                    className="w-4 h-4 rounded border-[#1A1B1A]/20 text-[#205B5A]" />
                  <div className="flex items-center gap-2 flex-1">
                    <Globe className="w-4 h-4 text-[#757675]" />
                    <div>
                      <p className="text-sm font-medium text-[#1A1B1A]">All appointments</p>
                      <p className="text-xs text-[#757675]">Required for every appointment</p>
                    </div>
                  </div>
                </label>
                
                <label className="flex items-center gap-3 p-3 rounded-xl border border-[#1A1B1A]/[0.06] cursor-pointer hover:bg-[#FAF9F6]">
                  <input type="checkbox" checked={appliesToNew} onChange={e => setAppliesToNew(e.target.checked)} 
                    className="w-4 h-4 rounded border-[#1A1B1A]/20 text-[#205B5A]" />
                  <div className="flex items-center gap-2 flex-1">
                    <UserPlus className="w-4 h-4 text-[#757675]" />
                    <div>
                      <p className="text-sm font-medium text-[#1A1B1A]">New patients</p>
                      <p className="text-xs text-[#757675]">Required for first-time patients</p>
                    </div>
                  </div>
                </label>
                
                <label className="flex items-center gap-3 p-3 rounded-xl border border-[#1A1B1A]/[0.06] cursor-pointer hover:bg-[#FAF9F6]">
                  <input type="checkbox" checked={appliesToReturning} onChange={e => setAppliesToReturning(e.target.checked)} 
                    className="w-4 h-4 rounded border-[#1A1B1A]/20 text-[#205B5A]" />
                  <div className="flex items-center gap-2 flex-1">
                    <User className="w-4 h-4 text-[#757675]" />
                    <div>
                      <p className="text-sm font-medium text-[#1A1B1A]">Returning patients</p>
                      <p className="text-xs text-[#757675]">Required for patients with previous visits</p>
                    </div>
                  </div>
                </label>
                
                <label className="flex items-center gap-3 p-3 rounded-xl border border-[#1A1B1A]/[0.06] cursor-pointer hover:bg-[#FAF9F6]">
                  <input type="checkbox" checked={appliesToUnder18} onChange={e => setAppliesToUnder18(e.target.checked)} 
                    className="w-4 h-4 rounded border-[#1A1B1A]/20 text-[#205B5A]" />
                  <div className="flex items-center gap-2 flex-1">
                    <Baby className="w-4 h-4 text-[#757675]" />
                    <div>
                      <p className="text-sm font-medium text-[#1A1B1A]">Under 18</p>
                      <p className="text-xs text-[#757675]">Required for minor patients</p>
                    </div>
                  </div>
                </label>
              </div>
            </div>

            {/* Minor / guardian consent */}
            <div className="mb-6">
              <h3 className="text-xs font-semibold text-[#757675] uppercase tracking-wider mb-3">Under-18 patients</h3>
              <div className="space-y-2" data-testid="minor-mode-options">
                {[
                  { value: "auto", title: "Minor-aware (recommended)", desc: "Guardian consent section appears automatically when the patient is under 18" },
                  { value: "always", title: "Always require guardian consent", desc: "Every patient must have a parent or guardian sign this form" },
                  { value: "never", title: "Standard patient form", desc: "Never show the guardian consent section" },
                ].map(opt => (
                  <label key={opt.value} className="flex items-start gap-3 p-3 rounded-xl border border-[#1A1B1A]/[0.06] cursor-pointer hover:bg-[#FAF9F6]">
                    <input
                      type="radio"
                      name="minor-mode"
                      value={opt.value}
                      checked={minorMode === opt.value}
                      onChange={() => setMinorMode(opt.value)}
                      data-testid={`minor-mode-${opt.value}`}
                      className="w-4 h-4 mt-0.5 text-[#205B5A]"
                    />
                    <div>
                      <p className="text-sm font-medium text-[#1A1B1A]">{opt.title}</p>
                      <p className="text-xs text-[#757675]">{opt.desc}</p>
                    </div>
                  </label>
                ))}
              </div>
            </div>

            {/* Appointment Types */}
            {apptTypes.length > 0 && (
              <div className="mb-6">
                <h3 className="text-xs font-semibold text-[#757675] uppercase tracking-wider mb-3">Specific Appointment Types</h3>
                <p className="text-xs text-[#757675] mb-3">Select appointment types that require this form:</p>
                <div className="space-y-1.5 max-h-48 overflow-y-auto">
                  {apptTypes.map(at => (
                    <label key={at.id} className="flex items-center gap-3 p-2.5 rounded-lg border border-[#1A1B1A]/[0.06] cursor-pointer hover:bg-[#FAF9F6]">
                      <input 
                        type="checkbox" 
                        checked={assignedApptTypes.includes(at.id)} 
                        onChange={() => toggleApptType(at.id)}
                        className="w-4 h-4 rounded border-[#1A1B1A]/20 text-[#205B5A]" 
                      />
                      <div className="flex items-center gap-2 flex-1">
                        <div className="w-3 h-3 rounded-full" style={{ backgroundColor: at.color || "#205B5A" }} />
                        <span className="text-sm text-[#1A1B1A]">{at.name}</span>
                      </div>
                      <span className="text-xs text-[#757675]">{at.duration_minutes}min</span>
                    </label>
                  ))}
                </div>
              </div>
            )}

            <div className="flex justify-end gap-3 pt-4 border-t border-[#1A1B1A]/[0.06]">
              <button onClick={() => setShowSettings(false)} data-testid="form-settings-close-btn" className="px-4 py-2 text-sm text-[#757675] hover:text-[#1A1B1A]">
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

/* ── Form Fill / Preview ── */
function FormFill({ template, patientId, appointmentId, onClose, onSubmitted }) {
  const [responses, setResponses] = useState({});
  const [signatureData, setSignatureData] = useState(null); // { type: "drawn"|"typed", data: string }
  const [submitting, setSubmitting] = useState(false);
  const [validationError, setValidationError] = useState("");
  const signatureRef = useRef(null);

  // Check if form has signature field
  const hasSignatureField = template.fields?.some(f => f.field_type === "signature");
  const signatureField = template.fields?.find(f => f.field_type === "signature");
  const signatureRequired = signatureField?.required;

  const validateForm = () => {
    // Check required fields
    for (const f of template.fields || []) {
      if (f.required && f.field_type !== "heading" && f.field_type !== "info_text" && f.field_type !== "signature") {
        const val = responses[f.id];
        if (val === undefined || val === "" || val === null) {
          setValidationError(`Please complete the required field: ${f.label}`);
          return false;
        }
      }
    }
    // Check signature if required
    if (signatureRequired) {
      if (!signatureData || !signatureData.data) {
        setValidationError("Please provide your signature before submitting.");
        return false;
      }
    }
    setValidationError("");
    return true;
  };

  const handleSubmit = async () => {
    if (!validateForm()) return;
    
    setSubmitting(true);
    try {
      // Get signature data from ref if available (to get latest canvas state)
      let finalSignature = signatureData;
      if (signatureRef.current) {
        const sigData = signatureRef.current.getSignatureData();
        if (sigData) {
          finalSignature = sigData;
        }
      }

      const payload = {
        template_id: template.id,
        patient_id: patientId,
        appointment_id: appointmentId,
        responses,
        status: "completed",
        // New signature fields
        signature_type: finalSignature?.type || null,
        signature_data: finalSignature?.type === "drawn" ? finalSignature.data : null,
        typed_signature: finalSignature?.type === "typed" ? finalSignature.data : null,
        // Legacy field for backwards compatibility
        signature: finalSignature?.type === "typed" ? finalSignature.data : null,
      };

      await api.post("/forms/submissions", payload);
      onSubmitted();
    } catch (err) {
      setValidationError("Failed to submit form. Please try again.");
    }
    setSubmitting(false);
  };

  const handleSignatureChange = (data) => {
    setSignatureData(data.isEmpty ? null : data);
    if (validationError && data.data) {
      setValidationError("");
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-[#FAF9F6] flex flex-col" data-testid="form-fill">
      <div className="bg-white border-b border-[#1A1B1A]/[0.06] px-5 py-3 flex items-center justify-between flex-shrink-0">
        <div className="flex items-center gap-3">
          <button onClick={onClose} className="p-1 text-[#757675] hover:text-[#1A1B1A]"><X className="w-4 h-4" /></button>
          <h2 className="text-base font-medium text-[#1A1B1A]" style={H}>{template.name}</h2>
        </div>
        <button onClick={handleSubmit} disabled={submitting} data-testid="submit-form-btn" 
          className="bg-[#205B5A] text-white px-5 py-1.5 rounded-full text-[11px] font-medium hover:bg-[#174544] disabled:opacity-40">
          {submitting ? "Submitting..." : "Submit form"}
        </button>
      </div>

      {/* Validation error banner */}
      {validationError && (
        <div className="bg-red-50 border-b border-red-100 px-5 py-2.5" data-testid="validation-error">
          <p className="text-sm text-red-600">{validationError}</p>
        </div>
      )}

      <div className="flex-1 overflow-y-auto p-6">
        <div className="max-w-xl mx-auto space-y-4">
          {template.fields?.map((f, i) => (
            <div key={f.id || i}>
              {f.field_type === "heading" && <h3 className="text-lg font-medium text-[#1A1B1A] mt-4 mb-1" style={H}>{f.label}</h3>}
              {f.field_type === "info_text" && <p className="text-sm text-[#4A4B4A] leading-relaxed bg-[#F3F2EE] rounded-lg px-4 py-3">{f.label}</p>}
              {f.field_type === "short_text" && (
                <div><label className="text-xs font-medium text-[#1A1B1A] mb-1 block">{f.label} {f.required && <span className="text-red-400">*</span>}</label>
                <input value={responses[f.id] || ""} onChange={e => setResponses({...responses, [f.id]: e.target.value})} placeholder={f.placeholder} className="w-full px-3 py-2.5 rounded-xl border border-[#1A1B1A]/10 text-sm focus:outline-none focus:border-[#205B5A]" /></div>
              )}
              {f.field_type === "long_text" && (
                <div><label className="text-xs font-medium text-[#1A1B1A] mb-1 block">{f.label} {f.required && <span className="text-red-400">*</span>}</label>
                <textarea value={responses[f.id] || ""} onChange={e => setResponses({...responses, [f.id]: e.target.value})} rows={3} placeholder={f.placeholder} className="w-full px-3 py-2.5 rounded-xl border border-[#1A1B1A]/10 text-sm focus:outline-none focus:border-[#205B5A] resize-none" /></div>
              )}
              {f.field_type === "date" && (
                <div><label className="text-xs font-medium text-[#1A1B1A] mb-1 block">{f.label} {f.required && <span className="text-red-400">*</span>}</label>
                <input type="date" value={responses[f.id] || ""} onChange={e => setResponses({...responses, [f.id]: e.target.value})} className="w-full px-3 py-2.5 rounded-xl border border-[#1A1B1A]/10 text-sm focus:outline-none focus:border-[#205B5A]" /></div>
              )}
              {f.field_type === "dropdown" && (
                <div><label className="text-xs font-medium text-[#1A1B1A] mb-1 block">{f.label} {f.required && <span className="text-red-400">*</span>}</label>
                <select value={responses[f.id] || ""} onChange={e => setResponses({...responses, [f.id]: e.target.value})} className="w-full px-3 py-2.5 rounded-xl border border-[#1A1B1A]/10 text-sm focus:outline-none focus:border-[#205B5A]">
                  <option value="">Select...</option>{f.options?.map((o, oi) => <option key={oi} value={o}>{o}</option>)}</select></div>
              )}
              {f.field_type === "radio" && (
                <div><label className="text-xs font-medium text-[#1A1B1A] mb-2 block">{f.label} {f.required && <span className="text-red-400">*</span>}</label>
                <div className="space-y-1.5">{f.options?.map((o, oi) => (
                  <label key={oi} className="flex items-center gap-2 cursor-pointer"><input type="radio" name={f.id} value={o} checked={responses[f.id] === o} onChange={e => setResponses({...responses, [f.id]: e.target.value})} className="text-[#205B5A]" /><span className="text-sm text-[#4A4B4A]">{o}</span></label>
                ))}</div></div>
              )}
              {f.field_type === "checkbox" && (
                <label className="flex items-start gap-2.5 cursor-pointer"><input type="checkbox" checked={responses[f.id] || false} onChange={e => setResponses({...responses, [f.id]: e.target.checked})} className="rounded border-[#1A1B1A]/20 text-[#205B5A] mt-0.5" /><span className="text-sm text-[#4A4B4A]">{f.label} {f.required && <span className="text-red-400">*</span>}</span></label>
              )}
              {f.field_type === "signature" && (
                <SignaturePad
                  ref={signatureRef}
                  label={f.label}
                  required={f.required}
                  onChange={handleSignatureChange}
                  initialMode="draw"
                />
              )}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

/* ── Submission Detail View ── */
function SubmissionDetailView({ submissionId, onClose }) {
  const [submission, setSubmission] = useState(null);
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);
  const printRef = useRef(null);

  useEffect(() => {
    const loadSubmission = async () => {
      setLoading(true);
      try {
        const { data } = await api.get(`/forms/submissions/${submissionId}`);
        setSubmission(data);
      } catch (err) {
        console.error("Failed to load submission:", err);
      }
      setLoading(false);
    };
    loadSubmission();
  }, [submissionId]);

  const handleExportPDF = async () => {
    if (!printRef.current) return;
    setExporting(true);
    
    try {
      // Dynamically import html2pdf to avoid SSR issues
      const html2pdf = (await import("html2pdf.js")).default;
      
      const element = printRef.current;
      const opt = {
        margin: [10, 10, 10, 10],
        filename: `${submission?.template?.name || "form"}_${submission?.patient_name || "submission"}_${submission?.created_at?.split("T")[0] || "export"}.pdf`,
        image: { type: "jpeg", quality: 0.98 },
        html2canvas: { scale: 2, useCORS: true, logging: false },
        jsPDF: { unit: "mm", format: "a4", orientation: "portrait" },
      };
      
      await html2pdf().set(opt).from(element).save();
    } catch (err) {
      console.error("PDF export failed:", err);
    }
    
    setExporting(false);
  };

  const formatDateTime = (isoString) => {
    if (!isoString) return "—";
    const d = new Date(isoString);
    return d.toLocaleString("en-GB", {
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  if (loading) {
    return (
      <div className="fixed inset-0 z-50 bg-[#FAF9F6] flex items-center justify-center">
        <div className="text-sm text-[#757675]">Loading submission...</div>
      </div>
    );
  }

  if (!submission) {
    return (
      <div className="fixed inset-0 z-50 bg-[#FAF9F6] flex flex-col">
        <div className="bg-white border-b border-[#1A1B1A]/[0.06] px-5 py-3">
          <button onClick={onClose} className="p-1 text-[#757675] hover:text-[#1A1B1A]">
            <X className="w-4 h-4" />
          </button>
        </div>
        <div className="flex-1 flex items-center justify-center">
          <p className="text-sm text-[#757675]">Submission not found</p>
        </div>
      </div>
    );
  }

  const template = submission.template || submission.template_snapshot;
  const fields = template?.fields || [];
  const responses = submission.responses || {};

  return (
    <div className="fixed inset-0 z-50 bg-[#FAF9F6] flex flex-col" data-testid="submission-detail-view">
      {/* Header */}
      <div className="bg-white border-b border-[#1A1B1A]/[0.06] px-5 py-3 flex items-center justify-between flex-shrink-0">
        <div className="flex items-center gap-3">
          <button onClick={onClose} className="p-1 text-[#757675] hover:text-[#1A1B1A]" data-testid="close-submission-detail">
            <X className="w-4 h-4" />
          </button>
          <div>
            <h2 className="text-base font-medium text-[#1A1B1A]" style={H}>
              {template?.name || "Form Submission"}
            </h2>
            <p className="text-xs text-[#757675]">
              {submission.patient_name} · {formatDateTime(submission.created_at)}
            </p>
          </div>
        </div>
        <button
          onClick={handleExportPDF}
          disabled={exporting}
          data-testid="export-pdf-btn"
          className="bg-[#205B5A] text-white px-4 py-1.5 rounded-full text-[11px] font-medium hover:bg-[#174544] disabled:opacity-40 flex items-center gap-1.5"
        >
          <Download className="w-3.5 h-3.5" />
          {exporting ? "Exporting..." : "Export PDF"}
        </button>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-y-auto p-6">
        <div ref={printRef} className="max-w-2xl mx-auto bg-white rounded-2xl border border-[#1A1B1A]/[0.06] p-6 md:p-8">
          {/* Form header for PDF */}
          <div className="mb-6 pb-6 border-b border-[#1A1B1A]/[0.06]">
            <h1 className="text-xl font-medium text-[#1A1B1A] mb-1" style={H}>
              {template?.name || "Form Submission"}
            </h1>
            <p className="text-sm text-[#757675]">
              Submitted by {submission.patient_name} on {formatDateTime(submission.created_at)}
            </p>
            {submission.signature_type && (
              <div className="mt-2 flex items-center gap-2">
                <span className={`text-[9px] font-semibold px-2 py-0.5 rounded-full ${
                  submission.signature_type === "drawn" 
                    ? "bg-[#205B5A]/10 text-[#205B5A]" 
                    : "bg-[#496275]/10 text-[#496275]"
                }`}>
                  {submission.signature_type === "drawn" ? "Drawn signature" : "Typed signature"}
                </span>
              </div>
            )}
          </div>

          {/* Form responses */}
          <div className="space-y-5">
            {fields.map((f, i) => (
              <div key={f.id || i}>
                {f.field_type === "heading" && (
                  <h3 className="text-base font-medium text-[#1A1B1A] mt-4 mb-2" style={H}>
                    {f.label}
                  </h3>
                )}
                {f.field_type === "info_text" && (
                  <p className="text-sm text-[#4A4B4A] leading-relaxed bg-[#F3F2EE] rounded-lg px-4 py-3">
                    {f.label}
                  </p>
                )}
                {(f.field_type === "short_text" || f.field_type === "long_text" || f.field_type === "date") && (
                  <div className="mb-3">
                    <label className="text-xs font-medium text-[#757675] mb-1 block">
                      {f.label}
                    </label>
                    <p className="text-sm text-[#1A1B1A] bg-[#FAF9F6] rounded-lg px-3 py-2.5">
                      {responses[f.id]
                        ? (f.field_type === "date" ? ukDate(responses[f.id]) : responses[f.id])
                        : <span className="text-[#757675] italic">Not provided</span>}
                    </p>
                  </div>
                )}
                {f.field_type === "dropdown" && (
                  <div className="mb-3">
                    <label className="text-xs font-medium text-[#757675] mb-1 block">
                      {f.label}
                    </label>
                    <p className="text-sm text-[#1A1B1A] bg-[#FAF9F6] rounded-lg px-3 py-2.5">
                      {responses[f.id] || <span className="text-[#757675] italic">Not selected</span>}
                    </p>
                  </div>
                )}
                {f.field_type === "radio" && (
                  <div className="mb-3">
                    <label className="text-xs font-medium text-[#757675] mb-1 block">
                      {f.label}
                    </label>
                    <p className="text-sm text-[#1A1B1A] bg-[#FAF9F6] rounded-lg px-3 py-2.5">
                      {responses[f.id] || <span className="text-[#757675] italic">Not selected</span>}
                    </p>
                  </div>
                )}
                {f.field_type === "checkbox" && (
                  <div className="mb-3 flex items-start gap-2.5">
                    <div className={`w-4 h-4 rounded border mt-0.5 flex items-center justify-center ${
                      responses[f.id] ? "bg-[#205B5A] border-[#205B5A]" : "border-[#1A1B1A]/20"
                    }`}>
                      {responses[f.id] && (
                        <svg className="w-2.5 h-2.5 text-white" fill="currentColor" viewBox="0 0 12 12">
                          <path d="M10.28 2.28L3.989 8.575 1.695 6.28A1 1 0 00.28 7.695l3 3a1 1 0 001.414 0l7-7A1 1 0 0010.28 2.28z"/>
                        </svg>
                      )}
                    </div>
                    <span className="text-sm text-[#4A4B4A]">{f.label}</span>
                  </div>
                )}
                {f.field_type === "signature" && (
                  <div className="mb-3">
                    <label className="text-xs font-medium text-[#757675] mb-2 block">
                      {f.label}
                    </label>
                    <div className="border border-[#1A1B1A]/10 rounded-xl p-4 bg-white">
                      {submission.signature_type === "drawn" && submission.signature_data ? (
                        <div>
                          <img
                            src={submission.signature_data}
                            alt="Patient signature"
                            className="max-h-32 w-auto"
                            data-testid="drawn-signature-image"
                          />
                          <div className="mt-2 border-t border-[#1A1B1A]/10 pt-2">
                            <p className="text-[10px] text-[#757675]">
                              Drawn signature
                            </p>
                          </div>
                        </div>
                      ) : submission.signature_type === "typed" && submission.typed_signature ? (
                        <div>
                          <p
                            className="text-2xl text-[#1A1B1A] italic"
                            style={{ fontFamily: "'Brush Script MT', 'Segoe Script', 'Bradley Hand', cursive" }}
                            data-testid="typed-signature-text"
                          >
                            {submission.typed_signature}
                          </p>
                          <div className="mt-2 border-t border-[#1A1B1A]/10 pt-2">
                            <p className="text-[10px] text-[#757675]">
                              Typed signature
                            </p>
                          </div>
                        </div>
                      ) : submission.signature ? (
                        <div>
                          <p
                            className="text-2xl text-[#1A1B1A] italic"
                            style={{ fontFamily: "'Brush Script MT', 'Segoe Script', 'Bradley Hand', cursive" }}
                          >
                            {submission.signature}
                          </p>
                          <div className="mt-2 border-t border-[#1A1B1A]/10 pt-2">
                            <p className="text-[10px] text-[#757675]">
                              Electronic signature (legacy)
                            </p>
                          </div>
                        </div>
                      ) : (
                        <p className="text-sm text-[#757675] italic">No signature provided</p>
                      )}
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>

          {/* Parent / guardian consent (under-18 patients) */}
          <GuardianDetailsPanel submission={submission} />

          {/* Staff review: accept / reject the submitted form */}
          <div className="mt-6" data-html2canvas-ignore="true">
            <FormReviewPanel submission={submission} onReviewed={(updated) => setSubmission({ ...submission, ...updated })} />
          </div>

          {/* Signature audit trail (IP masked; full details permission-gated) */}
          <AuditTrailPanel submission={submission} title="Signature Audit Trail" />

          {/* Footer for PDF */}
          <div className="mt-8 pt-4 border-t border-[#1A1B1A]/[0.06] text-center">
            <p className="text-[10px] text-[#757675]">
              This document was generated from a digital form submission. The signature above constitutes legal consent.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}

/* ── Main Forms Page ── */
export default function FormsPage() {
  const [tab, setTab] = useState("templates");
  const [templates, setTemplates] = useState([]);
  const [submissions, setSubmissions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showBuilder, setShowBuilder] = useState(null);
  const [showFill, setShowFill] = useState(null);
  const [showPreview, setShowPreview] = useState(null);
  const [viewPdfTemplate, setViewPdfTemplate] = useState(null);
  const [showSubmissionDetail, setShowSubmissionDetail] = useState(null);
  const [showPDFUpload, setShowPDFUpload] = useState(false);

  const loadData = useCallback(async () => {
    setLoading(true);
    const [tRes, sRes] = await Promise.all([api.get("/forms/templates"), api.get("/forms/submissions")]);
    setTemplates(tRes.data.templates);
    setSubmissions(sRes.data.submissions);
    setLoading(false);
  }, []);

  useEffect(() => { loadData(); }, [loadData]);

  const handleSaveTemplate = async (data) => {
    if (showBuilder?.id) {
      await api.put(`/forms/templates/${showBuilder.id}`, data);
    } else {
      await api.post("/forms/templates", data);
    }
    setShowBuilder(null);
    loadData();
  };

  const handlePDFUploadSuccess = (template) => {
    setShowPDFUpload(false);
    loadData();
  };

  const duplicateTemplate = async (id) => { await api.post(`/forms/templates/${id}/duplicate`); loadData(); };
  const archiveTemplate = async (id) => { await api.delete(`/forms/templates/${id}`); loadData(); };

  // Count PDF vs built forms
  const pdfCount = templates.filter(t => t.is_uploaded_pdf).length;
  const builtCount = templates.filter(t => !t.is_uploaded_pdf).length;

  return (
    <div className="p-6 md:p-8 max-w-7xl" data-testid="forms-page">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-medium text-[#1A1B1A]" style={H}>Forms</h1>
          <p className="text-sm text-[#757675]">
            {templates.length} template{templates.length !== 1 ? "s" : ""} 
            {pdfCount > 0 && <span className="text-[#757675]/70"> ({pdfCount} PDF)</span>}
            {" · "}{submissions.length} submission{submissions.length !== 1 ? "s" : ""}
          </p>
        </div>
        <div className="flex items-center gap-3">
          <ContextualHelp helpKey="forms-assignment" />
          <button onClick={() => setShowPDFUpload(true)} data-testid="upload-form-btn"
            className="border border-[#205B5A]/20 text-[#205B5A] px-4 py-2 rounded-full text-sm font-medium hover:bg-[#205B5A]/5 flex items-center gap-1.5">
            <Upload className="w-4 h-4" /> Upload form
          </button>
          <button onClick={() => setShowBuilder({})} data-testid="create-form-btn"
            className="bg-[#205B5A] text-white px-4 py-2 rounded-full text-sm font-medium hover:bg-[#174544] flex items-center gap-1.5">
            <Plus className="w-4 h-4" /> Create form
          </button>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 mb-5 border-b border-[#1A1B1A]/[0.06]">
        {[{ k: "templates", l: "Templates" }, { k: "submissions", l: "Submissions" }].map(t => (
          <button key={t.k} onClick={() => setTab(t.k)} data-testid={`tab-${t.k}`}
            className={`px-4 py-2.5 text-sm font-medium border-b-2 -mb-px transition-colors ${tab === t.k ? "text-[#205B5A] border-[#205B5A]" : "text-[#757675] border-transparent hover:text-[#1A1B1A]"}`}>{t.l}
            {t.k === "templates" && templates.length > 0 && <span className="ml-1.5 text-[10px] bg-[#1A1B1A]/5 px-1.5 py-0.5 rounded-full">{templates.length}</span>}
            {t.k === "submissions" && submissions.length > 0 && <span className="ml-1.5 text-[10px] bg-[#1A1B1A]/5 px-1.5 py-0.5 rounded-full">{submissions.length}</span>}
          </button>
        ))}
      </div>

      {/* Templates */}
      {tab === "templates" && (
        templates.length === 0 && !loading ? (
          <div className="bg-white rounded-2xl border border-[#1A1B1A]/[0.06] p-12 text-center">
            <FileCheck className="w-10 h-10 text-[#757675]/20 mx-auto mb-3" />
            <p className="text-sm text-[#757675] mb-1">No form templates yet</p>
            <p className="text-xs text-[#757675] mb-4">Create a form, generate with AI, or upload an existing PDF</p>
            <div className="flex items-center justify-center gap-3 flex-wrap">
              <button onClick={() => setShowBuilder({})} className="bg-[#205B5A] text-white px-4 py-2 rounded-full text-sm font-medium hover:bg-[#174544] flex items-center gap-1.5"><Plus className="w-3.5 h-3.5" /> Blank form</button>
              <button onClick={() => setShowBuilder({ aiStart: true })} className="border border-[#205B5A]/20 text-[#205B5A] px-4 py-2 rounded-full text-sm font-medium hover:bg-[#205B5A]/5 flex items-center gap-1.5"><Sparkles className="w-3.5 h-3.5" /> Generate with AI</button>
              <button onClick={() => setShowPDFUpload(true)} className="border border-[#1A1B1A]/10 text-[#4A4B4A] px-4 py-2 rounded-full text-sm font-medium hover:bg-[#FAF9F6] flex items-center gap-1.5"><Upload className="w-3.5 h-3.5" /> Upload PDF</button>
            </div>
          </div>
        ) : (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {templates.map(t => {
              const isActive = t.is_active !== false; // Default to active
              const hasAssignments = t.applies_to_all || (t.assigned_appointment_type_ids?.length > 0) || t.applies_to_new_patients || t.applies_to_returning_patients || t.applies_to_under_18;
              const isPDF = t.is_uploaded_pdf;
              
              return (
                <div key={t.id} className={`bg-white rounded-2xl border ${isActive ? "border-[#1A1B1A]/[0.06]" : "border-amber-200"} p-5 hover:shadow-sm transition-shadow group ${!isActive ? "opacity-70" : ""}`} data-testid={`template-${t.id}`}>
                  <div className="flex items-start justify-between mb-2">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        {isPDF && (
                          <div className="w-6 h-6 rounded bg-red-50 flex items-center justify-center flex-shrink-0" title="Uploaded PDF">
                            <FileText className="w-3.5 h-3.5 text-red-500" />
                          </div>
                        )}
                        <h3 className="text-sm font-medium text-[#1A1B1A] truncate">{t.name}</h3>
                        {!isActive && (
                          <span className="text-[8px] font-semibold px-1.5 py-0.5 rounded-full bg-amber-100 text-amber-700">Inactive</span>
                        )}
                      </div>
                      <div className="flex items-center gap-1.5 mt-1">
                        <span className={`text-[9px] font-semibold px-2 py-0.5 rounded-full capitalize ${TYPE_BADGE[t.form_type] || TYPE_BADGE.custom}`}>{t.form_type}</span>
                        {isPDF ? (
                          <span className="text-[10px] text-[#757675]">PDF · {t.pdf_requires_signature !== false ? "Signature required" : "View only"}</span>
                        ) : (
                          <span className="text-[10px] text-[#757675]">{t.fields?.length || 0} fields</span>
                        )}
                      </div>
                    </div>
                    {/* Active toggle */}
                    <button
                      onClick={async () => {
                        await api.post(`/forms/templates/${t.id}/toggle-active`);
                        loadData();
                      }}
                      className={`p-1.5 rounded-lg transition-colors ${isActive ? "text-[#205B5A] hover:bg-[#205B5A]/10" : "text-[#757675] hover:bg-[#FAF9F6]"}`}
                      title={isActive ? "Deactivate form" : "Activate form"}
                      data-testid={`toggle-active-${t.id}`}
                    >
                      {isActive ? <Eye className="w-3.5 h-3.5" /> : <EyeOff className="w-3.5 h-3.5" />}
                    </button>
                  </div>
                  
                  {/* Assignment badges */}
                  {hasAssignments && (
                    <div className="flex flex-wrap gap-1 mb-2">
                      {t.applies_to_all && (
                        <span className="text-[8px] font-medium px-1.5 py-0.5 rounded bg-[#205B5A]/10 text-[#205B5A]">All appointments</span>
                      )}
                      {t.assigned_appointment_type_ids?.length > 0 && (
                        <span className="text-[8px] font-medium px-1.5 py-0.5 rounded bg-[#496275]/10 text-[#496275]">{t.assigned_appointment_type_ids.length} appt type{t.assigned_appointment_type_ids.length > 1 ? "s" : ""}</span>
                      )}
                      {t.applies_to_new_patients && (
                        <span className="text-[8px] font-medium px-1.5 py-0.5 rounded bg-blue-50 text-blue-600">New patients</span>
                      )}
                      {t.applies_to_returning_patients && (
                        <span className="text-[8px] font-medium px-1.5 py-0.5 rounded bg-emerald-50 text-emerald-600">Returning</span>
                      )}
                      {t.applies_to_under_18 && (
                        <span className="text-[8px] font-medium px-1.5 py-0.5 rounded bg-purple-50 text-purple-600">Under 18</span>
                      )}
                    </div>
                  )}
                  
                  {t.description && <p className="text-xs text-[#757675] mb-3 line-clamp-2">{t.description}</p>}
                  
                  <div className="flex items-center gap-1.5 pt-2 border-t border-[#1A1B1A]/[0.04]">
                    {isPDF ? (
                      <>
                        <button
                          onClick={() => setViewPdfTemplate(t)}
                          className="text-[10px] font-medium text-[#205B5A] hover:text-[#174544] px-2 py-1 rounded-lg hover:bg-[#205B5A]/5 flex items-center gap-1"
                          data-testid={`view-pdf-${t.id}`}
                        >
                          <Eye className="w-3 h-3" /> View PDF
                        </button>
                        <button onClick={() => archiveTemplate(t.id)} className="text-[10px] text-[#757675] hover:text-red-500 px-2 py-1 rounded-lg ml-auto"><Trash2 className="w-3 h-3" /></button>
                      </>
                    ) : (
                      <>
                        <button onClick={() => setShowBuilder(t)} data-testid={`edit-template-${t.id}`} className="text-[10px] font-medium text-[#205B5A] hover:text-[#174544] px-2 py-1 rounded-lg hover:bg-[#205B5A]/5">Edit</button>
                        <button onClick={() => duplicateTemplate(t.id)} className="text-[10px] font-medium text-[#4A4B4A] hover:text-[#1A1B1A] px-2 py-1 rounded-lg hover:bg-[#FAF9F6] flex items-center gap-1"><Copy className="w-3 h-3" /> Duplicate</button>
                        <button onClick={() => setShowPreview(t)} className="text-[10px] font-medium text-[#4A4B4A] hover:text-[#1A1B1A] px-2 py-1 rounded-lg hover:bg-[#FAF9F6] flex items-center gap-1"><Eye className="w-3 h-3" /> Preview</button>
                        <button onClick={() => archiveTemplate(t.id)} className="text-[10px] text-[#757675] hover:text-red-500 px-2 py-1 rounded-lg ml-auto"><Trash2 className="w-3 h-3" /></button>
                      </>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )
      )}

      {/* Submissions */}
      {tab === "submissions" && (
        submissions.length === 0 ? (
          <div className="bg-white rounded-2xl border border-[#1A1B1A]/[0.06] p-12 text-center">
            <FileCheck className="w-10 h-10 text-[#757675]/20 mx-auto mb-3" />
            <p className="text-sm text-[#757675]">No form submissions yet</p>
          </div>
        ) : (
          <div className="bg-white rounded-2xl border border-[#1A1B1A]/[0.06] overflow-hidden">
            <table className="w-full">
              <thead><tr className="border-b border-[#1A1B1A]/[0.06]">
                <th className="text-left px-5 py-3 text-[10px] font-medium text-[#757675] uppercase">Form</th>
                <th className="text-left px-5 py-3 text-[10px] font-medium text-[#757675] uppercase hidden md:table-cell">Patient</th>
                <th className="text-left px-5 py-3 text-[10px] font-medium text-[#757675] uppercase hidden md:table-cell">Date</th>
                <th className="text-left px-5 py-3 text-[10px] font-medium text-[#757675] uppercase hidden sm:table-cell">Signature</th>
                <th className="text-left px-5 py-3 text-[10px] font-medium text-[#757675] uppercase">Status</th>
              </tr></thead>
              <tbody>{submissions.map(s => (
                <tr 
                  key={s.id} 
                  onClick={() => setShowSubmissionDetail(s.id)}
                  data-testid={`submission-row-${s.id}`}
                  className="border-b border-[#1A1B1A]/[0.03] hover:bg-[#FAF9F6] cursor-pointer"
                >
                  <td className="px-5 py-3">
                    <p className="text-sm font-medium text-[#1A1B1A]">{s.form_name}</p>
                    <span className={`text-[9px] font-semibold px-2 py-0.5 rounded-full ${TYPE_BADGE[s.form_type] || TYPE_BADGE.custom}`}>{s.form_type}</span>
                  </td>
                  <td className="px-5 py-3 text-sm text-[#4A4B4A] hidden md:table-cell">{s.patient_name || "—"}</td>
                  <td className="px-5 py-3 text-sm text-[#757675] hidden md:table-cell">{ukDate(s.created_at?.split("T")[0])}</td>
                  <td className="px-5 py-3 hidden sm:table-cell">
                    {s.signature_type === "drawn" ? (
                      <span className="text-[9px] font-semibold px-2 py-0.5 rounded-full bg-[#205B5A]/10 text-[#205B5A] flex items-center gap-1 w-fit">
                        <PenLine className="w-2.5 h-2.5" /> Drawn
                      </span>
                    ) : s.signature_type === "typed" ? (
                      <span className="text-[9px] font-semibold px-2 py-0.5 rounded-full bg-[#496275]/10 text-[#496275] flex items-center gap-1 w-fit">
                        <Type className="w-2.5 h-2.5" /> Typed
                      </span>
                    ) : s.signature ? (
                      <span className="text-[9px] font-semibold px-2 py-0.5 rounded-full bg-[#757675]/10 text-[#757675]">Legacy</span>
                    ) : (
                      <span className="text-[10px] text-[#757675]">—</span>
                    )}
                  </td>
                  <td className="px-5 py-3"><span className="text-[9px] font-semibold px-2 py-0.5 rounded-full bg-[#205B5A]/10 text-[#205B5A] capitalize">{s.status}</span></td>
                </tr>
              ))}</tbody>
            </table>
          </div>
        )
      )}

      {/* Builder */}
      {showBuilder !== null && <FormBuilder initial={showBuilder} onSave={handleSaveTemplate} onClose={() => setShowBuilder(null)} />}

      {/* Preview as fill */}
      {viewPdfTemplate && <PdfTemplateViewer template={viewPdfTemplate} onClose={() => setViewPdfTemplate(null)} />}
      {showPreview && <FormFill template={showPreview} onClose={() => setShowPreview(null)} onSubmitted={() => { setShowPreview(null); loadData(); }} />}

      {/* Submission Detail */}
      {showSubmissionDetail && <SubmissionDetailView submissionId={showSubmissionDetail} onClose={() => setShowSubmissionDetail(null)} />}

      {/* PDF Upload Modal */}
      {showPDFUpload && <PDFUploadModal onClose={() => setShowPDFUpload(false)} onSuccess={handlePDFUploadSuccess} />}
    </div>
  );
}
